import React, { Fragment, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Form, { Field } from '@atlaskit/form';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import GradientRovoIcon from '../../common/assets/GradientRovoIcon';
import {
	type GenerateImageProps,
	IMAGE_GENERATION_THEMES,
	type ImageGenerationTheme,
} from '../../types';

const i18n = defineMessages({
	getCreativeLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-label',
		defaultMessage: 'Get creative with Rovo',
		description: 'Label text for the Rovo header generation entry point',
	},
	getCreativePlaceholder: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-placeholder',
		defaultMessage: 'Describe an image to create',
		description: 'Placeholder text for the Rovo header generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.rovo-header-image-generator.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alt text for the Rovo icon',
	},
	submitButtonLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.submit-button-label',
		defaultMessage: 'Generate AI header image',
		description: 'Label for the Rovo header generation submit button',
	},
	pickStyleLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.pick-style-label',
		defaultMessage: 'Pick a style',
		description: 'Label for the style/theme picker dropdown',
	},
	styleLayered: {
		id: 'ai-image-generation.rovo-header-image-generator.style-layered',
		defaultMessage: 'Layered',
		description: 'Style option: Layered (atlassian_v4)',
	},
	styleOutline: {
		id: 'ai-image-generation.rovo-header-image-generator.style-outline',
		defaultMessage: 'Outline',
		description: 'Style option: Outline (atlassian_explore)',
	},
	styleVibrant: {
		id: 'ai-image-generation.rovo-header-image-generator.style-vibrant',
		defaultMessage: 'Vibrant',
		description: 'Style option: Vibrant (atlassian_build)',
	},
});

interface ImageGenerationFieldData {
	promptInput: string;
}

type RovoHeaderImageGeneratorProps = {
	deletePreviousHeaderImage?: () => void;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
};

const styles = cssMap({
	divider: {
		width: '100%',
		height: '1px',
		marginBlock: token('space.075'),
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	rovoIcon: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.100'),
		marginBlockStart: token('space.150'),
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
});

export const RovoHeaderImageGenerator = ({
	generateAIHeaderImage,
	deletePreviousHeaderImage,
}: RovoHeaderImageGeneratorProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedTheme, setSelectedTheme] = useState<{
		label: string;
		value: ImageGenerationTheme;
	} | null>(null);
	const { trackAIInteractionInit, trackAIResultView, trackAIResultError } =
		useAIEventsInstrumentation({
			product: 'confluence',
			subproduct: 'confluence',
			aiFeatureName: 'aiHeaderImageGeneration',
			proactiveGeneratedAI: 0,
			userGeneratedAI: 1,
			enablePlatformEvents: true,
			source: 'rovoHeaderImageGenerator',
		});

	const themeOptions = [
		{ label: formatMessage(i18n.styleLayered), value: IMAGE_GENERATION_THEMES.ATLASSIAN_V4 },
		{ label: formatMessage(i18n.styleOutline), value: IMAGE_GENERATION_THEMES.ATLASSIAN_EXPLORE },
		{ label: formatMessage(i18n.styleVibrant), value: IMAGE_GENERATION_THEMES.ATLASSIAN_BUILD },
	];

	/* IMPORTANT: The experiment `confluence_aifc_image_generation_experiment` must always be gated behind `aifc_create_enabled`
	on Statsig. Otherwise, this component will be shown outside of the AIFC release */
	const isExperimentEnabled = FeatureGates.getExperimentValue(
		'confluence_aifc_image_generation_experiment',
		'isEnabled',
		false,
	);

	if (!generateAIHeaderImage || !isExperimentEnabled) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.divider} />

			{fg('platform-design_system_team-form_conversion') ? (
				<Form
					onSubmit={async (data: ImageGenerationFieldData) => {
						const useRawPrompt = !selectedTheme?.value;

						// Track that user initiated AI image generation
						trackAIInteractionInit({
							attributes: {
								promptLength: data.promptInput?.length || 0,
								useRawPrompt,
								selectedTheme: selectedTheme?.value || 'none',
							},
						});

						deletePreviousHeaderImage?.();

						try {
							// Generate the AI header image
							await generateAIHeaderImage({
								adfContent: data.promptInput,
								useRawPrompt,
								...(selectedTheme?.value && { theme: selectedTheme.value }),
							});

							// Track successful result view
							trackAIResultView({
								attributes: {
									promptLength: data.promptInput?.length || 0,
									useRawPrompt,
									selectedTheme: selectedTheme?.value || 'none',
								},
							});
						} catch (error) {
							// Track error if generation fails
							trackAIResultError({
								aiErrorMessage: error instanceof Error ? error.message : 'Unknown error',
								aiErrorCode: (error as any)?.code,
							});
						}
					}}
				>
					<Inline space="space.075" alignBlock="center">
						<Box xcss={styles.rovoIcon}>
							<GradientRovoIcon label={formatMessage(i18n.rovoIconAlt)} />
						</Box>
						<label htmlFor="get-creative-text-area">
							<Text size="medium" id="get-creative-label">
								{formatMessage(i18n.getCreativeLabel)}
							</Text>
						</label>
					</Inline>

					<Stack space="space.100">
						<Box>
							<label htmlFor="theme-picker">
								<Text size="small" weight="semibold">
									{formatMessage(i18n.pickStyleLabel)}
								</Text>
							</label>
							<Box paddingBlockStart="space.050">
								<Select
									inputId="theme-picker"
									options={themeOptions}
									value={selectedTheme}
									onChange={(newValue) => setSelectedTheme(newValue)}
									placeholder={formatMessage(i18n.pickStyleLabel)}
								/>
							</Box>
						</Box>
					</Stack>

					<Box xcss={styles.imagePromptContainer}>
						{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
						<Box xcss={styles.stack}>
							<Field isRequired name="promptInput" id="get-creative-text-area">
								{({ fieldProps }: any) => (
									<Fragment>
										<TextArea
											placeholder={formatMessage(i18n.getCreativePlaceholder)}
											appearance="none"
											{...fieldProps}
										/>
									</Fragment>
								)}
							</Field>
							<Inline space="space.075" alignInline="end">
								<IconButton
									appearance="primary"
									icon={ArrowUpIcon}
									label={formatMessage(i18n.submitButtonLabel)}
									shape="circle"
									type="submit"
								/>
							</Inline>
						</Box>
					</Box>
				</Form>
			) : (
				<Form
					onSubmit={async (data: ImageGenerationFieldData) => {
						const useRawPrompt = !selectedTheme?.value;

						// Track that user initiated AI image generation
						trackAIInteractionInit({
							attributes: {
								promptLength: data.promptInput?.length || 0,
								useRawPrompt,
								selectedTheme: selectedTheme?.value || 'none',
							},
						});

						deletePreviousHeaderImage?.();

						try {
							// Generate the AI header image
							await generateAIHeaderImage({
								adfContent: data.promptInput,
								useRawPrompt,
								...(selectedTheme?.value && { theme: selectedTheme.value }),
							});

							// Track successful result view
							trackAIResultView({
								attributes: {
									promptLength: data.promptInput?.length || 0,
									useRawPrompt,
									selectedTheme: selectedTheme?.value || 'none',
								},
							});
						} catch (error) {
							// Track error if generation fails
							trackAIResultError({
								aiErrorMessage: error instanceof Error ? error.message : 'Unknown error',
								aiErrorCode: (error as any)?.code,
							});
						}
					}}
				>
					{({ formProps }) => (
						<form {...formProps}>
							<Inline space="space.075" alignBlock="center">
								<Box xcss={styles.rovoIcon}>
									<GradientRovoIcon label={formatMessage(i18n.rovoIconAlt)} />
								</Box>
								<label htmlFor="get-creative-text-area">
									<Text size="medium" id="get-creative-label">
										{formatMessage(i18n.getCreativeLabel)}
									</Text>
								</label>
							</Inline>

							<Stack space="space.100">
								<Box>
									<label htmlFor="theme-picker">
										<Text size="small" weight="semibold">
											{formatMessage(i18n.pickStyleLabel)}
										</Text>
									</label>
									<Box paddingBlockStart="space.050">
										<Select
											inputId="theme-picker"
											options={themeOptions}
											value={selectedTheme}
											onChange={(newValue) => setSelectedTheme(newValue)}
											placeholder={formatMessage(i18n.pickStyleLabel)}
										/>
									</Box>
								</Box>
							</Stack>

							<Box xcss={styles.imagePromptContainer}>
								{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
								<Box xcss={styles.stack}>
									<Field isRequired name="promptInput" id="get-creative-text-area">
										{({ fieldProps }: any) => (
											<Fragment>
												<TextArea
													placeholder={formatMessage(i18n.getCreativePlaceholder)}
													appearance="none"
													{...fieldProps}
												/>
											</Fragment>
										)}
									</Field>
									<Inline space="space.075" alignInline="end">
										<IconButton
											appearance="primary"
											icon={ArrowUpIcon}
											label={formatMessage(i18n.submitButtonLabel)}
											shape="circle"
											type="submit"
										/>
									</Inline>
								</Box>
							</Box>
						</form>
					)}
				</Form>
			)}
		</>
	);
};
